/** The link numbers in words, the same on the robot page and in the headset's menu. */
import type { Perf, PerfEvent, Spread } from "./types";

export const msText = (v: number | null | undefined) => (v === null || v === undefined ? "—" : `${Math.round(v)} ms`);

/** "42 ms (p95 60)": the typical value, and how bad the bad tenth of a second gets. */
export const spreadText = (s: Spread | undefined) => {
  if (!s) return "not measured";
  const tail = s.p95 ?? s.p99;
  return tail === undefined ? msText(s.p50) : `${msText(s.p50)} (p95 ${Math.round(tail)})`;
};

const WHAT: Record<PerfEvent["kind"], string> = {
  input_gap: "controller frames stopped",
  input_lost: "no controller frames",
  video_freeze: "video froze",
  headset_hitch: "headset stalled",
  loop_late: "arm's control tick late",
  camera_gap: "camera skipped",
};

export const eventText = (e: PerfEvent) =>
  `${e.camera ? `${e.camera} ` : ""}${WHAT[e.kind] ?? e.kind} for ${msText(e.ms)}`;

export const agoText = (s: number) => (s < 60 ? `${Math.max(0, Math.round(s))} s ago` : `${Math.round(s / 60)} min ago`);

/**
 * The controls' latency: measured one way when both ends have the relay's clock, otherwise half
 * the round trip -- which needs no shared clock, and is the honest estimate when there is none.
 */
export function controlsMs(perf: Pick<Perf, "controls" | "rtt_ms">): { ms: number | null; estimated: boolean } {
  const oneWay = perf.controls?.ms?.p50;
  if (oneWay !== undefined && oneWay !== null) return { ms: oneWay, estimated: false };
  const rtt = perf.rtt_ms?.p50;
  return rtt !== undefined && rtt !== null ? { ms: rtt / 2, estimated: true } : { ms: null, estimated: false };
}

/** "~8 ms" for an estimate, "42 ms" for a measurement. */
export const estText = ({ ms, estimated }: { ms: number | null; estimated: boolean }) =>
  `${estimated && ms !== null ? "~" : ""}${msText(ms)}`;

/** One line for a small space: the two latencies the operator feels, and the stutters. */
export function linkLine(perf: Perf): string {
  const parts = [`controls ${estText(controlsMs(perf))}`, `video ${msText(perf.video?.ms?.p50)}`];
  parts.push(`${perf.stutters_1m} stutter${perf.stutters_1m === 1 ? "" : "s"}/min`);
  return parts.join(" · ");
}
